// src/contexts/OrdersContext.tsx
import React, { createContext, useContext, useState, useEffect, type ReactNode, useCallback } from 'react';
import { ordersAPI } from '../api/orders';
import type { Order } from '../types';
import { useAuth } from './AuthContext';

interface OrdersContextType {
  orders: Order[];
  isLoading: boolean;
  error: string | null;
  fetchOrders: () => Promise<void>;
  getOrder: (orderNumber: string) => Promise<Order | null>;
  cancelOrder: (orderNumber: string, reason: string) => Promise<boolean>;
  trackOrder: (trackingNumber: string) => Promise<any>;
  downloadInvoice: (orderNumber: string) => Promise<void>;
  addOrder: (order: Order) => void;
  updateOrderStatus: (orderNumber: string, status: Order['status']) => void;
}

const OrdersContext = createContext<OrdersContextType | undefined>(undefined);

export const useOrders = () => {
  const context = useContext(OrdersContext);
  if (!context) {
    throw new Error('useOrders must be used within OrdersProvider');
  }
  return context;
};

export const OrdersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const addOrder = useCallback((newOrder: Order) => {
    setOrders(prev => [newOrder, ...prev]); // Add to beginning
  }, []);

  const updateOrderStatus = useCallback((orderNumber: string, status: Order['status']) => {
    setOrders(prev => prev.map(order =>
      order.order_number === orderNumber
        ? { ...order, status }
        : order
    ));
  }, []);

  const fetchOrders = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const response = await ordersAPI.getMyOrders();
      if (response.data) {
        setOrders(response.data);
      } else if (response.error) {
        setError(response.error);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getOrder = async (orderNumber: string): Promise<Order | null> => {
    try {
      const response = await ordersAPI.getOrder(orderNumber);
      return response.data || null;
    } catch (error: any) {
      setError(error.message);
      return null;
    }
  };

  const cancelOrder = async (orderNumber: string, reason: string): Promise<boolean> => {
    try {
      const response = await ordersAPI.cancelOrder(orderNumber);
      if (response.data) {
        // Update local state
        setOrders(prev => prev.map(order =>
          order.order_number === orderNumber ? response.data! : order
        ));
        return true;
      }
      return false;
    } catch (error: any) {
      setError(error.message);
      return false;
    }
  };

  const trackOrder = async (trackingNumber: string): Promise<any> => {
    try {
      const response = await ordersAPI.trackOrder(trackingNumber);
      return response.data;
    } catch (error: any) {
      setError(error.message);
      return null;
    }
  };

  const downloadInvoice = async (orderNumber: string) => {
    try {
      const blob = await ordersAPI.downloadInvoice(orderNumber);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${orderNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      setError(error.message);
    }
  };

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  return (
    <OrdersContext.Provider value={{
      orders,
      isLoading,
      error,
      fetchOrders,
      getOrder,
      cancelOrder,
      trackOrder,
      downloadInvoice,
      addOrder,
      updateOrderStatus,
    }}>
      {children}
    </OrdersContext.Provider>
  );
};